import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faXmark, faCircleInfo, faCircleCheck, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import { guestApi, getGuestSession } from '../../utils/portalApi';
import { SERVER_URL } from '../../utils/api';
import Avatar from '../Avatar';
import MessageBubble from '../chat/MessageBubble';
import MessageComposer from '../chat/MessageComposer';
import GroupIcon from '../chat/GroupIcon';
import GuestChatInfo from './GuestChatInfo';

/**
 * One of the guest's group conversations (guest role, Phase 1).
 *
 * The thread the invite-link portal used to draw, now reached through the
 * guest's own session: every request names the group in the url, and the
 * server checks it against the guest's membership each time (see
 * guestChatAuth), so switching groups is just a different id.
 *
 * The server never sends this an employee's full record (utils/portalShape.js),
 * so the people here are only ever a name and a picture.
 */

/**
 * The portal's message shape, mapped onto the one MessageBubble draws, so the
 * attachments, lightbox, voice notes and quoted replies all come with it.
 */
export const toBubble = (m) => ({
    ...m,
    sender: m.author?.external ? null : { _id: m.author?._id, name: m.author?.name, profilePic: m.author?.profilePic },
    externalSender: m.author?.external ? { _id: m.author.externalId, name: m.author.name } : null,
    mentions: [],
    readBy: []
});

const daySeparator = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * embedded  drawn inside the messenger's right pane (GuestMessenger) rather
 *           than as a page of its own.
 * onBack    the phone layout's way back to the list.
 */
const GuestChat = ({ conversationId, onGone, onActivity, embedded = false, onBack }) => {
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [showStrip, setShowStrip] = useState(true);
    // The details pane: false, or { person } - null for the group itself.
    const [info, setInfo] = useState(false);
    const threadRef = useRef(null);
    const currentRef = useRef(conversationId);
    currentRef.current = conversationId;

    // Kept in refs so the socket effect does not reconnect on every render.
    const goneRef = useRef(onGone);
    goneRef.current = onGone;
    const activityRef = useRef(onActivity);
    activityRef.current = onActivity;

    const base = `/chats/${conversationId}`;
    const meId = conversation?.me?._id;

    const scrollToBottom = (behavior = 'auto') => {
        requestAnimationFrame(() => {
            const el = threadRef.current;
            if (el) el.scrollTo({ top: el.scrollHeight, behavior });
        });
    };

    const markRead = () => {
        guestApi.post(`${base}/read`).then(() => activityRef.current?.()).catch(() => { });
    };

    const lost = (err) => {
        if ([401, 403, 404].includes(err.response?.status)) {
            goneRef.current?.(err.response?.data?.message || 'This conversation is no longer available to you.');
        }
    };

    useEffect(() => {
        let cancelled = false;
        setConversation(null);
        setMessages([]);
        setReplyTo(null);
        setInfo(false);
        setLoading(true);
        setLoadingMore(false);

        (async () => {
            try {
                const [{ data: convo }, { data: page }] = await Promise.all([
                    guestApi.get(base),
                    guestApi.get(`${base}/messages`)
                ]);
                if (cancelled) return;
                setConversation(convo);
                setMessages(page.messages);
                setHasMore(page.hasMore);
                scrollToBottom();
                markRead();
            } catch (err) {
                if (!cancelled) lost(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId]);

    // A role or history change made by the team, picked up without a reload.
    useEffect(() => {
        if (!meId) return undefined;
        const timer = setInterval(() => {
            guestApi.get(base).then(({ data }) => {
                if (currentRef.current === conversationId) setConversation(data);
            }).catch(lost);
        }, 30000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meId, conversationId]);

    /* Live updates over the guest's own socket session. */
    useEffect(() => {
        if (!meId) return undefined;
        const session = getGuestSession();
        if (!session) return undefined;

        const socket = io(SERVER_URL || window.location.origin, {
            auth: { token: session },
            transports: ['websocket', 'polling']
        });

        socket.on('connect', () => socket.emit('conversation:join', conversationId));

        socket.on('message:new', (m) => {
            setMessages((prev) => (prev.some((x) => x._id === m._id) ? prev : [...prev, m]));
            scrollToBottom('smooth');
            markRead();
        });
        socket.on('message:edited', (m) => {
            setMessages((prev) => prev.map((x) => (x._id === m._id ? m : x)));
        });
        // A video the server finished converting - see ChatWindow.js.
        socket.on('message:media', ({ messageId, mediaId, ...patch }) => {
            setMessages((prev) => prev.map((x) => (String(x._id) !== String(messageId) ? x : {
                ...x,
                attachments: (x.attachments || []).map((a) => (
                    String(a._id) === String(mediaId) ? { ...a, ...patch } : a
                ))
            })));
        });
        socket.on('message:deleted', ({ _id }) => {
            // The row stays so the thread keeps its shape; the words do not.
            setMessages((prev) => prev.map((x) => (
                x._id === _id ? { ...x, deletedAt: new Date().toISOString(), text: '', attachments: [] } : x
            )));
        });
        socket.on('external:revoked', (payload) => {
            if (String(payload.participantId) !== String(meId)) return;
            setMessages([]);
            goneRef.current?.('Your access to this conversation has been withdrawn.');
        });
        // Switched off company-wide. The home page's next check signs them out.
        socket.on('guest:disabled', () => {
            setMessages([]);
            goneRef.current?.('Your guest access has been switched off.');
        });
        socket.on('external:updated', ({ participantId }) => {
            if (String(participantId) !== String(meId)) return;
            guestApi.get(base).then(({ data }) => {
                if (currentRef.current === conversationId) setConversation(data);
            }).catch(lost);
        });

        return () => {
            socket.emit('conversation:leave', conversationId);
            socket.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meId, conversationId]);

    const loadOlder = async () => {
        if (!messages.length || loadingMore) return;
        setLoadingMore(true);
        try {
            const res = await guestApi.get(`${base}/messages`, { params: { before: messages[0].createdAt } });
            if (currentRef.current !== conversationId) return;
            const el = threadRef.current;
            const before = el?.scrollHeight || 0;
            setMessages((prev) => [...res.data.messages, ...prev]);
            setHasMore(res.data.hasMore);
            // Hold the reading position while older messages arrive above it.
            requestAnimationFrame(() => {
                if (el) el.scrollTop = el.scrollHeight - before;
            });
        } catch {
            /* the older page simply does not arrive */
        } finally {
            setLoadingMore(false);
        }
    };

    const send = async ({ text, files, audioMeta, replyToId }) => {
        const form = new FormData();
        form.append('text', text || '');
        if (replyToId) form.append('replyToId', replyToId);
        (files || []).forEach((f) => form.append('attachments', f));
        if (audioMeta) {
            form.append('durationMs', audioMeta.durationMs || 0);
            form.append('waveform', JSON.stringify(audioMeta.waveform || []));
        }

        const res = await guestApi.post(`${base}/messages`, form, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (currentRef.current !== conversationId) return;
        setMessages((prev) => (prev.some((m) => m._id === res.data._id) ? prev : [...prev, res.data]));
        setReplyTo(null);
        scrollToBottom('smooth');
        activityRef.current?.();
    };

    if (!conversation) {
        return (
            <div className={`portal-chat ${embedded ? 'guest-embedded' : ''}`}>
                <div className="portal-frame">
                    <div className="portal-empty">
                        <div className="portal-spinner" />
                        <p>{loading ? 'Loading the conversation…' : 'This conversation is not available.'}</p>
                    </div>
                </div>
            </div>
        );
    }

    let lastDay = null;
    const team = conversation.members || [];
    const faces = team.slice(0, 4);
    const moreFaces = team.length - faces.length;
    const viewer = conversation.me.role === 'viewer';
    const myUserId = String(conversation.me.userId || '');

    // A name tapped in the thread: that person's card. Never their own.
    const openAuthor = (bubble) => {
        if (bubble.externalSender) {
            if (String(bubble.externalSender._id) === myUserId) return;
            setInfo({ person: { kind: 'guest', _id: bubble.externalSender._id } });
        } else if (bubble.sender?._id) {
            setInfo({ person: { kind: 'staff', _id: bubble.sender._id } });
        }
    };

    return (
        <div className={`portal-chat ${embedded ? 'guest-embedded' : ''} ${info ? 'has-info' : ''}`}>
            <div className="portal-frame">
                <header className="portal-head">
                    {onBack && (
                        <button type="button" className="msgr-icon-btn guest-back" aria-label="Back to chats" onClick={onBack}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                    )}
                    {conversation.isDirect
                        ? <Avatar name={conversation.name} profilePic={conversation.avatar} className="msgr-avatar" />
                        : <GroupIcon conversation={{ kind: conversation.isApp ? 'app' : 'group', name: conversation.name, avatar: conversation.avatar }} className="msgr-avatar" />}
                    <button type="button" className="portal-head-body guest-head-btn"
                        aria-label={conversation.isDirect ? `About ${conversation.name}` : 'Group details'}
                        onClick={() => setInfo(info ? false : { person: null })}>
                        <h1>{conversation.name}</h1>
                        <div className="portal-head-sub">
                            {conversation.projectName && (
                                <>
                                    <span>{conversation.projectName}</span>
                                    <i className="portal-dot" />
                                </>
                            )}
                            <span>{conversation.isApp
                                ? 'Notifications from a connected app'
                                : conversation.isDirect ? 'GTS team · tap for details' : `${team.length} ${team.length === 1 ? 'person' : 'people'} from the team · tap for details`}</span>
                        </div>
                    </button>

                    {/* Who is on the other side, so a guest is not writing into
                        what looks like an empty inbox. */}
                    {!conversation.isDirect && faces.length > 0 && (
                        <div className="portal-faces" role="button" tabIndex={0} aria-label="Group members"
                            onClick={() => setInfo({ person: null })}
                            onKeyDown={(e) => { if (e.key === 'Enter') setInfo({ person: null }); }}>
                            {faces.map((u) => (
                                <Avatar key={u._id} name={u.name} profilePic={u.profilePic} className="msgr-avatar" />
                            ))}
                            {moreFaces > 0 && <div className="portal-faces-more">+{moreFaces}</div>}
                        </div>
                    )}
                </header>

                {showStrip && (
                    <div className="portal-strip">
                        <FontAwesomeIcon icon={faCircleInfo} />
                        <p>
                            {conversation.isApp
                                ? `Notifications from ${conversation.name}. Only you can see this chat.`
                                : conversation.isDirect
                                    ? `A private chat between you and ${conversation.name}.`
                                    : <>
                                        You are a guest in this group, with {viewer ? 'view-only access' : 'permission to reply and upload'}.
                                        {' '}{conversation.me.shareHistory ? 'Earlier chat history is included.' : 'Only messages since your invitation are visible.'}
                                    </>}
                        </p>
                        <button onClick={() => setShowStrip(false)} title="Dismiss">
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                )}

                <div className="msgr-thread portal-thread" ref={threadRef}>
                    {loading && (
                        <div className="portal-empty">
                            <div className="portal-spinner" />
                            <p>Loading the conversation…</p>
                        </div>
                    )}

                    {!loading && hasMore && (
                        <button className="msgr-load-more" onClick={loadOlder} disabled={loadingMore}>
                            {loadingMore ? 'Loading…' : 'Load earlier messages'}
                        </button>
                    )}

                    {!loading && !messages.length && (
                        <div className="portal-empty">
                            <div className="portal-empty-icon"><FontAwesomeIcon icon={faCommentDots} /></div>
                            <h3>No messages yet</h3>
                            <p>Messages shared with you in this group will appear here.</p>
                        </div>
                    )}

                    {messages.map((m) => {
                        const day = daySeparator(m.createdAt);
                        const showDay = day !== lastDay;
                        lastDay = day;
                        const mine = Boolean(m.author?.external
                            && String(m.author.externalId) === String(conversation.me.userId));
                        return (
                            <React.Fragment key={m._id}>
                                {showDay && <div className="msgr-daysep">{day}</div>}
                                <MessageBubble
                                    id={`m-${m._id}`}
                                    message={toBubble(m)}
                                    mine={mine}
                                    showAuthor
                                    firstOfRun
                                    memberCount={0}
                                    onReply={viewer ? undefined : setReplyTo}
                                    onAuthorClick={conversation.isApp ? undefined : openAuthor}
                                />
                            </React.Fragment>
                        );
                    })}
                </div>

                <div className="portal-composer">
                    {viewer ? (
                        <p className="portal-composer-note">
                            {conversation.isApp
                                ? 'This chat only receives notifications from the app.'
                                : 'View only. A group admin can enable replies and uploads.'}
                        </p>
                    ) : (
                        <>
                            <MessageComposer
                                key={conversation._id}
                                conversation={conversation}
                                /* No mentions: @-tagging needs the employee
                                   directory, which a guest does not get. */
                                members={[]}
                                replyTo={replyTo ? toBubble(replyTo) : null}
                                onCancelReply={() => setReplyTo(null)}
                                onSend={send}
                            />
                            <p className="portal-composer-note">
                                <FontAwesomeIcon icon={faCircleCheck} />
                                Messages and files you send here go to the project team at GTS.
                            </p>
                        </>
                    )}
                </div>
            </div>
            {info && (
                <GuestChatInfo
                    conversation={conversation}
                    person={info.person}
                    onClose={() => setInfo(false)}
                />
            )}
        </div>
    );
};

export default GuestChat;
